import { Box, ChakraProvider, createLocalStorageManager, Flex, useColorMode } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useHotkeys } from "react-hotkeys-hook";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import AppDrawer from "./components/AppDrawer";
import HashRedirects from "./components/HashRedirects";
import Map from "./components/Map";
import MapSearch from "./components/search/MapSearch";
import AuthorisedApolloProvider from "./context/apollo.context";
import CognitoAuthProvider from "./context/auth.context";
import { FavouritesProvider } from "./context/favourites.context";
import { MapProvider } from "./context/map.context";
import AttendanceForensics from "./pages/attendance-forensics/AttendanceForensics";
import BookDesk from "./pages/booking/BookDesk";
import BookingSearch from "./pages/booking/BookingSearch";
import CheckIn from "./pages/booking/CheckIn";
import ViewBooking from "./pages/booking/ViewBooking";
import BusRoute from "./pages/buses/BusRoute";
import BusStopInformation from "./pages/buses/BusStopInformation";
import NearbyBusStops from "./pages/buses/NearbyBusStops";
import Directions from "./pages/directions/Directions";
import AllEvents from "./pages/events/AllEvents";
import ViewEvent from "./pages/events/ViewEvent";
import AllLaundrettes from "./pages/laundrettes/AllLaundrettes";
import ViewLaundrette from "./pages/laundrettes/ViewLaundrette";
import AllVenues from "./pages/musicFestival/AllVenues";
import GigsAtVenue from "./pages/musicFestival/GigsAtVenue";
import GigSearch from "./pages/musicFestival/GigSearch";
import SearchedVenue from "./pages/musicFestival/SearchedVenue";
import ViewGig from "./pages/musicFestival/ViewGig";
import AllOutlets from "./pages/outlets/AllOutlets";
import ViewOutlet from "./pages/outlets/ViewOutlet";
import AllPCs from "./pages/pcs/AllPCs";
import ViewPC from "./pages/pcs/ViewPC";
import HiddenGems from "./pages/hidden-gems/HiddenGems";
import ViewHiddenGem from "./pages/hidden-gems/ViewHiddenGem";
import Search from "./pages/search/Search";
import ViewWelcomeEvent from "./pages/welcomeProgramme/ViewWelcomeEvent";
import ViewWelcomePins from "./pages/welcomeProgramme/ViewWelcomePins";
import WelcomeEvents from "./pages/welcomeProgramme/WelcomeEvents";
import theme, { colourModeKey } from "./theme";

const queryClient = new QueryClient();

export const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setColorMode } = useColorMode();
  useHotkeys("esc", () => navigate("/"));

  useEffect(() => {
    const theme = new URLSearchParams(location.search).get("theme");
    if ((theme && theme === "light") || theme === "dark") {
      setColorMode(theme);
    }
  }, [location.search, setColorMode]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname,
    });
  }, [location]);

  return (
    <QueryClientProvider client={queryClient}>
      <CognitoAuthProvider>
        <AuthorisedApolloProvider>
          <FavouritesProvider>
            <MapProvider>
              <Flex>
                <Box flex={1}>
                  <Routes>
                    {/* SEARCH */}
                    <Route
                      index
                      element={
                        <>
                          <Search />
                          <AppDrawer />
                          <HashRedirects />
                        </>
                      }
                    />
                    <Route path="/location/:type/:id" element={<Search />} />
                    {/* BOOKINGS */}
                    <Route path="/book" element={<BookingSearch />} />
                    <Route
                      path="/book/:plannonId/:deskId"
                      element={<BookDesk />}
                    />
                    <Route path="/book/:plannonId/" element={<BookDesk />} />
                    <Route
                      path="/book/my-bookings/:bookingId"
                      element={<ViewBooking />}
                    />
                    <Route
                      path="/book/check-in/:deskId"
                      element={<CheckIn />}
                    />
                    {/* WELCOME WEEK */}
                    <Route
                      path="/welcomeprogramme"
                      element={<WelcomeEvents />}
                    />
                    <Route
                      path="/welcomeprogramme/:location"
                      element={<ViewWelcomePins />}
                    />
                    <Route
                      path="/welcomeprogramme/event/:eventID/:latitude/:longitude"
                      element={<ViewWelcomeEvent />}
                    />
                    {/* MUSIC FESTIVAL */}
                    <Route path="/musicfestival" element={<AllVenues />} />
                    <Route
                      path="/musicfestival/:locationId"
                      element={<GigsAtVenue />}
                    />
                    <Route
                      path="/musicfestival/:locationId/:gigId"
                      element={<ViewGig />}
                    />
                    <Route
                      path="/musicfestival/search"
                      element={<GigSearch />}
                    />
                    <Route
                      path="/musicfestival/search/:locationId"
                      element={<SearchedVenue />}
                    />
                    {/* EVENTS */}
                    <Route path="/events" element={<AllEvents />} />
                    <Route path="/events/:eventId" element={<ViewEvent />} />
                    {/* BUSES */}
                    <Route path="/buses" element={<NearbyBusStops />} />
                    <Route
                      path="/buses/:atcoCode"
                      element={<BusStopInformation />}
                    />
                    <Route
                      path="/buses/:atcoCode/:line"
                      element={<BusRoute />}
                    />
                    {/* LAUNDRETTES */}
                    <Route path="/laundry" element={<AllLaundrettes />} />
                    <Route
                      path="/laundry/:location"
                      element={<ViewLaundrette />}
                    />
                    {/* OUTLETS */}
                    <Route path="/outlets" element={<AllOutlets />} />
                    <Route
                      path="/outlets/:businessID"
                      element={<ViewOutlet />}
                    />
                    {/* PCS */}
                    <Route path="/pcs" element={<AllPCs />} />
                    <Route path="/pcs/:id" element={<ViewPC />} />
                    {/* DIRECTIONS */}
                    <Route
                      path="/directions/:from/:to"
                      element={<Directions />}
                    />
                    <Route
                      path="/attendance-forensics"
                      element={<AttendanceForensics />}
                    />
                    {/* HIDDEN GEMS */}
                    <Route
                      path="/hidden-gems"
                      element={<HiddenGems />}
                    />
                    <Route
                      path="/hidden-gems/:id"
                      element={<ViewHiddenGem />}
                    />
                  </Routes>
                  <Map />
                </Box>
              </Flex>
            </MapProvider>
          </FavouritesProvider>
        </AuthorisedApolloProvider>
      </CognitoAuthProvider>
    </QueryClientProvider>
  );
};
